import React, { lazy, Suspense } from 'react';
import AppContent, { ProtectedRoute } from '../AppContent/AppContent';
import LandingPage from '../components/LandingPage/LandingPage';
import OrganizerRoute from './OrganizerRoute/OrganizerRoute';
import Seo from '../components/Seo/Seo';
import RouteErrorFallback from '../Catch/RouteErrorFallback';
import PageSkeleton from '../components/Ui/PageSkeleton/PageSkeleton';

// Route-level code splitting: only LandingPage (and the structural
// AppContent/ProtectedRoute/OrganizerRoute wrappers above) are needed to
// render '/', so everything else loads on demand — same React.lazy +
// Suspense pattern already used for TicketVerification below.
const AdminPage = lazy(() => import('../components/Admin/AdminPage'));
const PostEditorPage = lazy(() => import('../components/Admin/PostManagement/PostEditorPage'));
const PlaceEditorPage = lazy(() => import('../components/Admin/CompanyManagement/PlaceEditorPage'));
const AdminPlaceDetailPage = lazy(() => import('../components/Admin/CompanyManagement/AdminPlaceDetailPage'));
const SettingsPage = lazy(() => import('../components/Settings/Settings'));
const PrivacyPolicy = lazy(() => import('../components/PrivacyPolicy/PrivacyPolicy'));
const AboutPage = lazy(() => import('../components/About/AboutPage'));
const Checkout = lazy(() => import('../components/Events/Purchase/Checkout/Checkout'));
const Confirmation = lazy(() => import('../components/Events/Purchase/Payment/Confirmation'));
const PasswordReset = lazy(() => import('../components/PasswordReset/PasswordReset'));
const PlacesListPage = lazy(() => import('../components/Places/PlacesListPage'));
const PlaceDetailPage = lazy(() => import('../components/Places/PlaceDetailPage'));
const GuidesListPage = lazy(() => import('../components/Guides/GuidesListPage'));
const GuideDetailPage = lazy(() => import('../components/Guides/GuideDetailPage'));
const TicketVerification = lazy(() => import('../components/TicketVerification/TicketVerification'));
const NotFound = lazy(() => import('../components/NotFound/NotFound'));

// One consistent fallback for every lazy route below. Full-viewport
// (min-height: 100dvh, see PageSkeleton) so that while the route's chunk
// downloads the placeholder fills the screen and the site footer — a
// sibling of the routed content in AppContent — stays below the fold
// instead of briefly flashing into view under a near-empty page.
const routeFallback = <PageSkeleton />;

/**
 * Defines the application's route configuration.
 *
 * @constant
 * @type {Array<Object>}
 */
const routes = [
  {
    path: '/',
    element: <AppContent />,
    errorElement: <RouteErrorFallback />,
    children: [
      {
        index: true,
        element: <LandingPage />,
      },
      // The member area ('home') is disabled — the route is removed so
      // /home resolves to the '*' NotFound route below, and its /^\/home$/
      // pattern is removed from server/utils/knownSpaRoutes.js too. The
      // HomePage / MemberNav component files are left in place for the
      // planned rewrite. To re-enable, restore this route object, the
      // HomePage lazy import above, and the server pattern.
      {
        path: 'admin',
        element: (
          <ProtectedRoute redirectTo="/">
            <Suspense fallback={routeFallback}>
              <AdminPage />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'admin/posts/new',
        element: (
          <ProtectedRoute redirectTo="/" adminOnly>
            <Suspense fallback={routeFallback}>
              <PostEditorPage />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'admin/posts/:id/edit',
        element: (
          <ProtectedRoute redirectTo="/" adminOnly>
            <Suspense fallback={routeFallback}>
              <PostEditorPage />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'admin/places/new',
        element: (
          <ProtectedRoute redirectTo="/" adminOnly>
            <Suspense fallback={routeFallback}>
              <PlaceEditorPage />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'admin/places/:id/edit',
        element: (
          <ProtectedRoute redirectTo="/" adminOnly>
            <Suspense fallback={routeFallback}>
              <PlaceEditorPage />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        // Internal merchant detail view. isAdmin-only (adminOnly). Ranks
        // below 'admin/places/new' (static) and 'admin/places/:id/edit'
        // (more segments), so those keep matching first.
        path: 'admin/places/:id',
        element: (
          <ProtectedRoute redirectTo="/" adminOnly>
            <Suspense fallback={routeFallback}>
              <AdminPlaceDetailPage />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'settings',
        element: (
          <ProtectedRoute redirectTo="/">
            <Suspense fallback={routeFallback}>
              <SettingsPage />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'privacy-policy',
        element: (
          <Suspense fallback={routeFallback}>
            <PrivacyPolicy />
          </Suspense>
        ),
      },
      {
        path: 'about',
        element: (
          <Suspense fallback={routeFallback}>
            <AboutPage />
          </Suspense>
        ),
      },
      // Events pages disabled — /events, /events/:id and /events/:slug now
      // fall through to the '*' NotFound route below. Their <EventListPage>
      // / <EventPage> lazy imports are removed above. Keep in sync with
      // server/utils/knownSpaRoutes.js (the /events patterns are removed
      // there too). To re-enable, restore all three route objects, the two
      // imports, and the server patterns.
      {
        path: 'places/:city',
        element: (
          <Suspense fallback={routeFallback}>
            <PlacesListPage />
          </Suspense>
        ),
      },
      {
        // Vibe / "occasion" filtered view of a city's places — same
        // PlacesListPage component, which reads the :vibe param. The literal
        // 'mood' segment avoids colliding with 'places/:city/:slug' below.
        // Mirrored in server/utils/knownSpaRoutes.js.
        path: 'places/:city/mood/:vibe',
        element: (
          <Suspense fallback={routeFallback}>
            <PlacesListPage />
          </Suspense>
        ),
      },
      {
        path: 'places/:city/:slug',
        element: (
          <Suspense fallback={routeFallback}>
            <PlaceDetailPage />
          </Suspense>
        ),
      },
      {
        path: 'guides',
        element: (
          <Suspense fallback={routeFallback}>
            <GuidesListPage />
          </Suspense>
        ),
      },
      {
        path: 'guides/:city',
        element: (
          <Suspense fallback={routeFallback}>
            <GuidesListPage />
          </Suspense>
        ),
      },
      {
        path: 'guides/:city/:slug',
        element: (
          <Suspense fallback={routeFallback}>
            <GuideDetailPage />
          </Suspense>
        ),
      },
      {
        path: 'checkout',
        // Seo is applied here, not inside Checkout.js — that component is
        // also embedded (with real props) inside the public EventPage.js
        // purchase flow, so a noindex tag baked into the component itself
        // would leak onto public /events/:id pages whenever the checkout
        // step is open. Scoping it to this standalone route keeps it off
        // Checkout.js entirely.
        element: (
          <Suspense fallback={routeFallback}>
            <Seo noindex />
            <Checkout />
          </Suspense>
        ),
      },
      {
        path: 'confirmation/:orderId',
        element: (
          <Suspense fallback={routeFallback}>
            <Confirmation />
          </Suspense>
        ),
      },
      {
        path: 'verify-ticket',
        element: (
          <OrganizerRoute>
            <Suspense fallback={<div>Loading QR Scanner...</div>}>
              <TicketVerification />
            </Suspense>
          </OrganizerRoute>
        ),
      },
      {
        // Genuinely unknown paths — paired with server.js's SPA catch-all,
        // which now returns a real HTTP 404 (not a silent 200) for any path
        // that doesn't match a known route shape (see
        // server/utils/knownSpaRoutes.js), while still serving this same
        // shell so this route can render. Previously this just Navigate'd
        // back to "/" with no explanation; that's been replaced with an
        // actual not-found page so users (and the noindex tag it renders)
        // get an honest signal instead of a silent redirect.
        path: '*',
        element: (
          <Suspense fallback={routeFallback}>
            <NotFound />
          </Suspense>
        ),
      },
    ],
  },
  {
    path: '/reset-password/:token',
    element: (
      <Suspense fallback={routeFallback}>
        <PasswordReset />
      </Suspense>
    ),
    errorElement: <RouteErrorFallback />,
  }
];

export default routes;
